<script setup>
import { defineProps, defineEmits, computed, ref, watch } from 'vue'
import { useToast } from 'vue-toastification'

import ReservationDialogSeat from '@/components/journal/parent/concert/ReservationDialogSeat.vue'

import api from '@/api'
import { getErrors } from '@/errors'

const step = 100
const scale = 0.4

const toast = useToast()

const props = defineProps(['concertID', 'seats', 'labels'])
const emit = defineEmits(['createReservation', 'destroyReservation'])

const groupBy = (items, grouper) => {
  const grouped = {}
  const result = []

  for (const item of items) {
    const group = grouper(item)
    const key = JSON.stringify(group)
    if (!grouped[key]) {
      grouped[key] = []
      result.push({
        ...group,
        items: grouped[key]
      })
    }
    grouped[key].push(item)
  }

  return result
}

const rowLabels = computed(() => {
  const seatsGrouped = groupBy(props.seats, (seat) => ({
    sector: seat.sector_name,
    row: seat.row
  }))

  const labels = []

  for (const { row, items: seats } of seatsGrouped) {
    const leftmostSeat = seats.reduce((curr, seat) => seat.x < (curr ? curr.x : Infinity) ? seat : curr, null)
    const rightmostSeat = seats.reduce((curr, seat) => seat.x > (curr ? curr.x : -Infinity) ? seat : curr, null)

    labels.push({
      row,
      style: {
        transform: `translate(-100%, -50%) translate(${hallRect.value.left + hallRect.value.padding.left}px, ${leftmostSeat.y}px)`,
        'text-align': 'right'
      }
    })
    labels.push({
      row,
      style: {
        transform: `translate(0, -50%) translate(${hallRect.value.right - hallRect.value.padding.right}px, ${rightmostSeat.y}px)`,
        'text-align': 'left'
      }
    })
  }

  return labels
})

const sectorLabels = computed(() => {
  const seatsGrouped = groupBy(props.seats, (seat) => ({
    sector: seat.sector_name
  }))

  const labels = []

  for (const { sector, items: seats } of seatsGrouped) {
    const leftmostSeat = seats.reduce((curr, seat) => seat.x < (curr ? curr.x : Infinity) ? seat : curr, null)
    const topmostSeat = seats.reduce((curr, seat) => seat.y < (curr ? curr.y : Infinity) ? seat : curr, null)

    labels.push({
      sector,
      style: {
        transform: `translate(-50%, -100%) translate(${leftmostSeat.x + 40}px, ${topmostSeat.y - step / 2}px)`,
        'text-align': 'center'
      }
    })
  }

  return labels
})

const labels = computed(() => {
  return props.labels.map((label) => ({
    id: label.id,
    text: label.text,
    hasBackground: label.has_background,
    style: {
      transform: `translate(${label.x}px, ${label.y}px)`,
      width: `${label.width}px`,
      height: `${label.height}px`,
      'font-size': `${label.font_size}px`
    }
  }))
})

const hallRect = computed(() => {
  const padding = {
    top: 104,
    right: 300,
    bottom: 150,
    left: 300
  }

  const left = props.seats.reduce((curr, seat) => Math.min(curr, seat.x), Infinity) - step / 2 - padding.left
  const right = props.seats.reduce((curr, seat) => Math.max(curr, seat.x), -Infinity) + step / 2 + padding.right

  const top = props.seats.reduce((curr, seat) => Math.min(curr, seat.y), Infinity) - step / 2 - padding.top
  const bottom = props.seats.reduce((curr, seat) => Math.max(curr, seat.y), -Infinity) + step / 2 + padding.bottom

  return {
    left,
    right,
    top,
    bottom,
    width: right - left,
    height: bottom - top,
    padding
  }
})

const hoveredSeat = ref()
const tooltipSeat = ref()

let hoveredSeatTimeoutID

watch(hoveredSeat, (_hoveredSeat) => {
  if (_hoveredSeat) {
    tooltipSeat.value = _hoveredSeat
  }

  clearTimeout(hoveredSeatTimeoutID)

  hoveredSeatTimeoutID = setTimeout(() => {
    tooltipSeat.value = null
  }, _hoveredSeat ? 3000 : 500)
})

const pendingSeats = ref([])
const addPendingSeat = (uuid) => { pendingSeats.value.push(uuid) }
const removePendingSeat = (uuid) => { pendingSeats.value = pendingSeats.value.filter((i) => i !== uuid) }
const isSeatPending = (uuid) => pendingSeats.value.includes(uuid)

const createReservation = (seatUUID) =>
  api.post('/reservations/', {
    concert: props.concertID,
    seat: seatUUID
  })

const destroyReservation = (reservationID) =>
  api.delete(`/reservations/${reservationID}/`)

const onSeatClick = (seat) => {
  if (isSeatPending(seat.uuid)) return

  if (!seat.is_available) {
    toast.error('Выбранное место недоступно к продаже')
    return
  }

  addPendingSeat(seat.uuid)

  let promise

  if (!seat.reservation) {
    promise = createReservation(seat.uuid)
      .then((response) => {
        emit('createReservation', response.data)
      }, (error) => {
        toast.error(getErrors(error).detail)
      })
  } else {
    promise = destroyReservation(seat.reservation.id)
      .then(() => {
        emit('destroyReservation', seat.reservation.id)
      }, (error) => {
        toast.error(getErrors(error).detail)
      })
  }

  promise.then(() => {
    removePendingSeat(seat.uuid)
  })
}
</script>

<template>
  <transition name="fade">
    <div v-if="tooltipSeat && tooltipSeat.is_available" class="seat-tooltip">
      <div>{{ tooltipSeat.sector_name }}</div>
      <div>Ряд <strong>{{ tooltipSeat.row }}</strong> Место <strong>{{ tooltipSeat.number }}</strong></div>
      <div>Цена <strong>{{ $utils.formatDecimal(tooltipSeat.price) }}</strong> ₽</div>
    </div>
  </transition>
  <div class="hall-wrapper">
    <div class="stage-wrapper">
      <div class="stage">
        <div class="stage-label">
          Сцена
        </div>
      </div>
    </div>
    <div
      class="hall"
      :style="{
        width: `${hallRect.width * scale}px`,
        height: `${hallRect.height * scale}px`
      }"
    >
      <div
        class="hall-data"
        :style="{
          transform: `scale(${scale}) translate(${-hallRect.left}px, ${-hallRect.top}px)`
        }"
      >
        <div class="seats">
          <ReservationDialogSeat
            v-for="seat in props.seats"
            :key="seat.uuid"
            :seat="seat"
            :isPending="isSeatPending(seat.uuid)"
            @click="onSeatClick(seat)"
            @mouseenter="hoveredSeat = seat"
            @mouseleave="hoveredSeat = null"
          />
        </div>
        <div class="row-labels">
          <div
            v-for="rowLabel in rowLabels"
            :key="rowLabel.row"
            :style="rowLabel.style"
            class="row-label"
          >
            Ряд {{ rowLabel.row }}
          </div>
        </div>
        <div class="sector-labels">
          <div
            v-for="sectorLabel in sectorLabels"
            :key="sectorLabel.sector"
            :style="sectorLabel.style"
            class="sector-label"
          >
            {{ sectorLabel.sector }}
          </div>
        </div>
        <div class="labels">
          <div
            v-for="label in labels"
            :key="label.id"
            :style="label.style"
            :class="['label', {'has-background': label.hasBackground}]"
          >
            {{ label.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hall-wrapper {
  width: max-content;
  user-select: none;
}

.hall {
  overflow: hidden;
}

.stage-wrapper {
  width: 100%;
  padding: 3em 4em;
}

.stage {
  border-radius: 1em;
  padding: 2em;

  background-color: var(--light-gray);

  display: flex;
  justify-content: center;
  align-items: center;
}

.stage-label {
  position: sticky;
  right: 20px;
  left: 20px;

  font-size: 200%;
  font-weight: bold;

  text-shadow: 0 1px 0 white;
}

.hall-data {
  position: relative;
  z-index: 1;

  width: 0;
  height: 0;
}

.seat-tooltip {
  position: fixed;

  z-index: 2;

  border-radius: 10px;
  background-color: rgba(255, 255, 255, 0.9);

  margin: 1em;
  padding: 1em;
}

.row-label {
  position: absolute;

  font-size: 35px;
  font-weight: bold;
  white-space: nowrap;

  padding: 0 40px;
}

.sector-label {
  position: absolute;

  font-size: 50px;
  font-weight: bold;
  white-space: nowrap;

  padding: 40px 0;
}

.label {
  position: absolute;

  font-weight: bold;

  display: flex;
  justify-content: center;
  align-items: center;
}

.label.has-background {
  border-radius: 1em;

  background-color: var(--light-gray);
}
</style>
